import { AlertCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** Accepts rich content so callers can emphasise names/values inline. */
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * Label shown on the confirm button while `isPending` is true. Defaults to
   * "Deleting…" because every caller that predates this prop is a delete
   * confirmation — pass something accurate for any other action.
   */
  pendingLabel?: string;
  destructive?: boolean;
  isPending?: boolean;
  /**
   * `default` — the compact dialog this component has always rendered.
   * `alert`   — the designer's confirmation popup (react-new-html
   *             `.sc-dashboard-list__modal-*`): blurred overlay, amber alert
   *             badge beside the title, larger card, filled Cancel button.
   * Existing callers omit this and are unaffected.
   */
  variant?: 'default' | 'alert';
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  pendingLabel = 'Deleting…',
  destructive = false,
  isPending = false,
  variant = 'default',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  if (!open) return null;

  const isAlert = variant === 'alert';

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className={
          isAlert ? 'absolute inset-0 bg-black/40 backdrop-blur-sm' : 'absolute inset-0 bg-black/40'
        }
        onClick={onCancel}
        aria-hidden="true"
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-desc"
        className={
          isAlert
            ? 'relative z-10 w-full max-w-md rounded-2xl border border-grayEF bg-white p-6 shadow-elevation-2 animate-modal'
            : 'relative z-10 w-full max-w-sm bg-white rounded-xl shadow-lg p-6 flex flex-col gap-4'
        }
      >
        {isAlert ? (
          <>
            <div className="mb-3 flex items-center gap-3 text-amber-500">
              <span className="flex items-center justify-center rounded-full bg-amber-50 p-2">
                <AlertCircle className="h-6 w-6" />
              </span>
              <h2 id="confirm-dialog-title" className="m-0 text-lg font-bold text-black-logo">
                {title}
              </h2>
            </div>
            <p id="confirm-dialog-desc" className="mb-6 text-sm leading-relaxed text-gray66">
              {description}
            </p>
          </>
        ) : (
          <div className="space-y-1.5">
            <h2
              id="confirm-dialog-title"
              className="text-sm font-semibold text-black-logo leading-5"
            >
              {title}
            </h2>
            <p id="confirm-dialog-desc" className="text-sm text-gray66 leading-[17px]">
              {description}
            </p>
          </div>
        )}

        <div
          className={
            isAlert
              ? 'flex items-center justify-end gap-3'
              : 'flex items-center justify-end gap-2.5'
          }
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className={
              isAlert
                ? 'rounded-lg border-none bg-grayEF px-4 py-2 text-sm font-medium text-gray30 transition-colors hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed'
                : 'btn btn-outline btn-small disabled:opacity-50 disabled:cursor-not-allowed'
            }
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className={
              destructive
                ? 'btn btn-small bg-red-500 hover:bg-red-600 text-white border-transparent disabled:opacity-50 disabled:cursor-not-allowed'
                : 'btn btn-primary btn-small disabled:opacity-50 disabled:cursor-not-allowed'
            }
          >
            {isPending ? pendingLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
